import { useAppStore } from '@store/appStore';

/**
 * History Button Component
 */
interface HistoryButtonProps {
  direction: 'back' | 'forward';
  onClick: () => void;
  disabled: boolean;
}

function HistoryButton({ direction, onClick, disabled }: HistoryButtonProps) {
  const icon = direction === 'back' ? (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M15 18l-6-6 6-6"/>
    </svg>
  ) : (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M9 18l6-6-6-6"/>
    </svg>
  );

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`history-button ${disabled ? 'disabled' : ''}`}
      aria-label={`${direction === 'back' ? 'Go back' : 'Go forward'} in navigation history`}
    >
      {icon}
    </button>
  );
}

/**
 * Navigation History Panel Component
 */
export function NavigationHistoryPanel() {
  const navigationHistory = useAppStore(state => state.navigationHistory);
  const goBackInHistory = useAppStore(state => state.goBackInHistory);
  const goForwardInHistory = useAppStore(state => state.goForwardInHistory);
  const clearHistory = useAppStore(state => state.clearHistory);

  const canGoBack = navigationHistory.currentIndex > 0;
  const canGoForward = navigationHistory.currentIndex < navigationHistory.steps.length - 1;

  const handleBack = () => {
    if (canGoBack) {
      goBackInHistory();
    }
  };

  const handleForward = () => {
    if (canGoForward) {
      goForwardInHistory();
    }
  };

  const handleClear = () => {
    if (confirm('Clear all navigation history? This cannot be undone.')) {
      clearHistory();
    }
  };

  return (
    <div className="navigation-history-panel">
      <div className="panel-header">
        <h3>Navigation History</h3>
        <div className="history-count">
          {navigationHistory.steps.length} {navigationHistory.steps.length === 1 ? 'journey' : 'journeys'}
        </div>
      </div>

      <div className="button-group">
        <HistoryButton
          direction="back"
          onClick={handleBack}
          disabled={!canGoBack}
        />
        <HistoryButton
          direction="forward"
          onClick={handleForward}
          disabled={!canGoForward}
        />
      </div>

      {/* Show current journey info */}
      {navigationHistory.steps.length > 0 && navigationHistory.currentIndex >= 0 && (
        <div className="current-journey">
          Journey: {navigationHistory.steps[navigationHistory.currentIndex].startFrame}→{navigationHistory.steps[navigationHistory.currentIndex].endFrame}
        </div>
      )}

      <button
        onClick={handleClear}
        className="clear-button"
        disabled={navigationHistory.steps.length === 0}
      >
        Clear History
      </button>
    </div>
  );
}